import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, NO_ERRORS_SCHEMA, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { EfxModalModule, ModalSize } from '@signal/component-library/efx-modal';
import { FieldFormHelperService } from 'src/app/helpers/field-form-helper.service';
import { LocalStorageService } from 'src/app/services/shared/local-storage/local-storage.service';

@Component({
  selector: 'app-modal-functions',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, EfxModalModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  templateUrl: './modal-functions.component.html',
  styleUrl: './modal-functions.component.scss',
})
export class ModalFunctionsComponent implements OnChanges {
  form: FormGroup;
  modalSize = ModalSize;
  @Input() functions: any;
  @Input() struct: any[] = [];
  @Input() data: any;
  @Input() openModal: boolean = false;
  @Input() title: string = '';
  @Input() showComentarioAudit: boolean = false;

  @Output() openModalChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onSave: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private formBuilder: FormBuilder,
    private localStorageService: LocalStorageService,
    private fieldHelper: FieldFormHelperService
  ) {
    this.form = this.formBuilder.group({
      functionFields: this.formBuilder.array([]),
      comentarioAudit: new FormControl(''),
    });
  }

  get functionFields(): FormArray {
    return this.form.get('functionFields') as FormArray;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['functions'] || (changes['openModal'] && this.openModal) || (changes['struct'] && this.openModal)) {
      this.initFunctionFields();
    }
  }

  private initFunctionFields(): void {

    this.functionFields.clear();

    const fields = this.functions?.functionFields;
    if (!Array.isArray(fields) || fields.length === 0) {
      return;
    }

    fields.forEach((fieldConfig: any) => {
      this.functionFields.push(this.createFunctionFieldGroup(fieldConfig));
    });

    // Reiniciar el estado de validación del formulario
    this.form.markAsUntouched();
    this.form.markAsPristine();
  }

  private createFunctionFieldGroup(fieldConfig: any): FormGroup {
    const type = fieldConfig?.type || 'custom';
    const structConfig = this.getStructConfigByField(fieldConfig?.field);
    const initialValue = type === 'input' ? '' : (fieldConfig?.value || '');
    const group = this.formBuilder.group({
      field: [fieldConfig?.field || '', Validators.required],
      type: [type, Validators.required],
      value: [initialValue],
      inputType: [structConfig?.inputType || 'text'],
      options: [Array.isArray(structConfig?.value) ? structConfig.value : []],
      validatorsMeta: [Array.isArray(structConfig?.validators) ? structConfig.validators : []],
      structPattern: [structConfig?.pattern || null],
      associateField: [structConfig?.associateField || null]
    });

    this.applyTypeBehavior(group, type);

    group.get('type')?.valueChanges.subscribe((newType: string) => {
      this.applyTypeBehavior(group, newType);
    });

    return group;
  }

  private applyTypeBehavior(group: FormGroup, type: string): void {
    const valueControl = group.get('value');
    if (!valueControl) {
      return;
    }

    if (type === 'date') {
      const structPattern = group.get('structPattern')?.value || null;
      valueControl.setValue(this.getTodayByPattern(structPattern), { emitEvent: false });
      valueControl.disable({ emitEvent: false });
      valueControl.clearValidators();
    } else if (type === 'user') {
      valueControl.setValue(this.getCurrentUser(), { emitEvent: false });
      valueControl.disable({ emitEvent: false });
      valueControl.clearValidators();
    } else if (type === 'input') {
      valueControl.enable({ emitEvent: false });
      valueControl.setValidators(this.buildInputValidators(group));
    } else if (type === 'custom') {
      valueControl.disable({ emitEvent: false });
      valueControl.clearValidators();
    } else {
      valueControl.disable({ emitEvent: false });
      valueControl.clearValidators();
    }

    valueControl.updateValueAndValidity({ emitEvent: false });
  }

  isEditableType(type: string): boolean {
    return type === 'input';
  }

  /**
   * Handles select change: updates the associated field (associateField) with the
   * selected option's description text, using FieldFormHelperService.
   */
  onSelectChange(fieldGroup: AbstractControl, selectedValue: string): void {
    const associateFieldName = fieldGroup?.get('associateField')?.value;
    if (!associateFieldName) return;

    const structField = { ...fieldGroup?.get('options') && { value: fieldGroup.get('options')?.value }, associateField: associateFieldName, inputType: 'select' };
    const descriptionText = this.fieldHelper.resolveAssociateText(structField, selectedValue) ?? '';

    const associatedGroup = this.functionFields.controls.find(
      ctrl => ctrl.get('field')?.value === associateFieldName
    );
    if (associatedGroup) {
      associatedGroup.get('value')?.setValue(descriptionText);
    }
  }

  getFieldLabel(fieldGroup: AbstractControl | null): string {
    const fieldName = fieldGroup?.get('field')?.value;
    const structConfig = this.getStructConfigByField(fieldName);
    return structConfig?.label || fieldName || '';
  }

  getInputType(fieldGroup: AbstractControl | null): string {
    return (fieldGroup?.get('inputType')?.value || 'text').toString().toLowerCase();
  }

  getSelectOptions(fieldGroup: AbstractControl | null): any[] {
    const options = fieldGroup?.get('options')?.value;
    return Array.isArray(options) ? options : [];
  }

  hasValueError(fieldGroup: AbstractControl | null): boolean {
    const control = fieldGroup?.get('value');
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  getValueErrorMessage(fieldGroup: AbstractControl | null): string {
    const control = fieldGroup?.get('value');
    if (!control || !control.errors) return '';
    const validatorsMeta = fieldGroup?.get('validatorsMeta')?.value;
    return this.fieldHelper.getValidatorErrorMessage(control, validatorsMeta);
  }

  private getStructConfigByField(fieldName: string): any {
    if (!fieldName || !Array.isArray(this.struct)) {
      return null;
    }

    const normalizedField = this.normalizeFieldKey(fieldName);
    return this.struct.find((item: any) => {
      const keys = [item?.name, item?.matchJournaling, item?.associateField, item?.label]
        .filter(Boolean)
        .map((key) => this.normalizeFieldKey(key));
      return keys.includes(normalizedField);
    }) || null;
  }

  private normalizeFieldKey(value: string): string {
    return (value || '').toString().trim().toUpperCase();
  }

  private buildInputValidators(group: FormGroup): ValidatorFn[] {
    const structField = {
      nullable: false, // input type always required
      pattern: group.get('structPattern')?.value,
      validators: group.get('validatorsMeta')?.value,
    };
    return [this.requiredTrimValidator(), ...this.fieldHelper.buildValidatorsFromStruct(structField)];
  }

  private requiredTrimValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value === null || value === undefined) return { required: true };
      if (typeof value === 'string' && value.trim().length === 0) return { required: true };
      return null;
    };
  }

  private getTodayByPattern(pattern: string | null): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;
    return this.fieldHelper.formatDateByPattern(isoDate, pattern);
  }

  private getCurrentUser(): string {
    return this.localStorageService.dataLogin?.getUser?.()?.toUpperCase?.() || 'SYSTEM';
  }

  onCancel(): void {
    this.openModal = false;
    this.openModalChange.emit(false);
    this.form.get('comentarioAudit')?.setValue('');
    this.form.reset();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.functionFields.controls.map((control: any) => {
      const raw = control.getRawValue();
      return {
        fieldToChange: raw.field,
        newValue: this.fieldHelper.formatDateByPattern(raw.value, raw.inputType === 'date' ? raw.structPattern : null),
        type: raw.type
      };
    });

    if (this.showComentarioAudit) {
      const comentarioAudit = this.form.get('comentarioAudit')?.value?.trim() || 'Funcionalidad';
      this.onSave.emit({ fieldsToUpdate: payload, comentarioAudit });
    } else {
      this.onSave.emit(payload);
    }

    this.onCancel();
  }
}
